import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import {
  Button,
  Calendar,
  EmptyState,
  SearchWithFilter,
  Tabs,
} from '@enamelai/design-system'
import { Icon as ArrowDownIcon } from '@enamelai/design-system/icons/Arrow_down'
import { Icon as CalendarIcon } from '@enamelai/design-system/icons/Calendar'
import NotificationColumn from './NotificationColumn.component'
import useNotifications from './useNotifications'
import { NotificationPriority } from '@dataTypes/notification.types'
import './NotificationsStyle.css'

const DATE_FORMAT = 'MMMM D, YYYY'

const TAB_ITEMS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'month', label: 'This month' },
]

const parseSelectedDate = (value: string): moment.Moment =>
  moment(value, DATE_FORMAT, true).isValid()
    ? moment(value, DATE_FORMAT)
    : moment(value)

function NotificationsComponent(): React.ReactElement {
  const hooks = useNotifications()
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false)
  const calendarWrapperRef = useRef<HTMLDivElement>(null)

  const selectedMoment = useMemo(
    () => parseSelectedDate(hooks.selectedDate),
    [hooks.selectedDate],
  )

  // A custom date was picked from the calendar (anything other than today)
  const isCustomDateSelected =
    selectedMoment.isValid() &&
    !selectedMoment.isSame(moment().startOf('day'), 'day')

  const hasNoNotifications = hooks.filteredNotifications.length === 0

  const toggleCalendar = useCallback(() => {
    setIsCalendarOpen((prev) => !prev)
  }, [])

  useEffect(() => {
    if (!isCalendarOpen) return undefined

    const handlePointerDown = (event: MouseEvent): void => {
      if (!calendarWrapperRef.current?.contains(event.target as Node)) {
        setIsCalendarOpen(false)
      }
    }
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setIsCalendarOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isCalendarOpen])

  const onDateSelect = (date: Date): void => {
    hooks.handleDateChange(moment(date).format(DATE_FORMAT))
    setIsCalendarOpen(false)
  }

  const dateDisplay = (): string => {
    if (isCustomDateSelected) {
      return selectedMoment.format('MMMM D, YYYY')
    }
    if (hooks.activeTab === 'yesterday') {
      return moment().subtract(1, 'day').format('MMMM D, YYYY')
    }
    if (hooks.activeTab === 'month') {
      return moment().format('MMMM YYYY')
    }
    return moment().format('MMMM D, YYYY')
  }

  return (
    <div className="notifications-page">
      <div className="notifications-header">
        <div className="notifications-header-left">
          <Tabs
            items={TAB_ITEMS}
            value={hooks.activeTab}
            onChange={hooks.handleChangeTab}
          />
          <span className="notifications-border-separator" aria-hidden="true" />
          <div className="calendar-toggle-wrapper" ref={calendarWrapperRef}>
            <Button
              variant="secondary"
              size="md"
              className="notifications-date-button"
              leftIcon={<CalendarIcon aria-hidden="true" />}
              rightIcon={<ArrowDownIcon aria-hidden="true" />}
              aria-haspopup="dialog"
              aria-expanded={isCalendarOpen}
              onClick={toggleCalendar}
            >
              {dateDisplay()}
            </Button>
            {isCalendarOpen && (
              <div className="calendar-popup-wrapper">
                <Calendar
                  value={
                    selectedMoment.isValid() ? selectedMoment.toDate() : null
                  }
                  defaultViewDate={
                    selectedMoment.isValid()
                      ? selectedMoment.toDate()
                      : new Date()
                  }
                  onChange={onDateSelect}
                />
              </div>
            )}
          </div>
        </div>

        <SearchWithFilter
          className="notifications-search-filter"
          placeholder="Search"
          ariaLabel="Search notifications"
          value={hooks.searchQuery}
          onChange={hooks.handleSearchChange}
          onClear={() => hooks.handleSearchChange('')}
          filters={hooks.filters}
          filterLabels={hooks.filterLabels}
          selectedFilters={hooks.selectedFilters}
          onFilterChange={hooks.handleFilterChange}
          onReset={hooks.handleFilterReset}
          showResetButton
        />
      </div>

      {hasNoNotifications ? (
        <div className="notifications-empty">
          <EmptyState
            outlined
            iconClass="mgc mgc_notification_fill"
            title="No notifications yet"
            description={
              isCustomDateSelected
                ? `No notifications for ${hooks.selectedDate}.`
                : 'No notifications found for this period.'
            }
            actionLabel={isCustomDateSelected ? 'View This Month' : undefined}
            onAction={
              isCustomDateSelected
                ? () => hooks.handleChangeTab('month')
                : undefined
            }
          />
        </div>
      ) : (
        <div className="notifications-columns-row">
          <NotificationColumn
            priority={NotificationPriority.CRITICAL}
            title="Critical"
            count={hooks.criticalCount}
            notifications={hooks.criticalNotifications}
            readNotifications={hooks.readNotifications}
            onMarkAsRead={hooks.handleMarkAsRead}
            onAction={hooks.handleAction}
          />
          <NotificationColumn
            priority={NotificationPriority.URGENT}
            title="Urgent"
            count={hooks.urgentCount}
            notifications={hooks.urgentNotifications}
            readNotifications={hooks.readNotifications}
            onMarkAsRead={hooks.handleMarkAsRead}
            onAction={hooks.handleAction}
          />
          <NotificationColumn
            priority={NotificationPriority.LOW}
            title="Low Priority"
            count={hooks.lowCount}
            notifications={hooks.lowNotifications}
            readNotifications={hooks.readNotifications}
            onMarkAsRead={hooks.handleMarkAsRead}
            onAction={hooks.handleAction}
          />
        </div>
      )}
    </div>
  )
}

export default NotificationsComponent
